import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Heart, ShoppingBag, User, Menu, X, Sun, Moon } from "lucide-react";

import Logo from "@/components/Logo";
import CartDrawer from "@/components/CartDrawer";
import SearchModal from "@/components/SearchModal";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";

const MEGA = {
  Women: {
    image: "https://images.unsplash.com/photo-1539109136881-3be0616acf4b?w=800&q=80",
    columns: [
      { title: "Edits", links: [["New In", "/shop/women?sort=newest"], ["Atelier Bestsellers", "/shop/women"], ["Limited Editions", "/shop/women"]] },
      { title: "Shop", links: [["Shirts", "/shop/women/shirts"], ["T-Shirts", "/shop/women/tshirts"], ["Trousers", "/shop/women/trousers"], ["Jackets", "/shop/women/jackets"]] },
      { title: "House", links: [["The Atelier", "/atelier"], ["Stories", "/stories"]] },
    ],
  },
  Men: {
    image: "https://images.unsplash.com/photo-1593030103066-0093718efeb9?w=800&q=80",
    columns: [
      { title: "Edits", links: [["New In", "/shop/men?sort=newest"], ["Atelier Bestsellers", "/shop/men"], ["Tailoring", "/shop/men/trousers"]] },
      { title: "Shop", links: [["Shirts", "/shop/men/shirts"], ["T-Shirts", "/shop/men/tshirts"], ["Trousers", "/shop/men/trousers"], ["Jackets", "/shop/men/jackets"]] },
      { title: "House", links: [["The Atelier", "/atelier"], ["Stories", "/stories"]] },
    ],
  },
};

// Animated underline link
const NavLink = ({ children, onMouseEnter, onClick, ...rest }) => (
  <button
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    className="ie-overline relative py-2 group transition-colors hover:text-brand-accent"
    {...rest}
  >
    {children}
    <span className="absolute bottom-1 left-0 right-0 h-px bg-current origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
  </button>
);

export default function Navbar() {
  const { theme, toggle: toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { count: cartCount, setDrawerOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const navigate = useNavigate();

  const handleCartClick = () => {
  if (!user) {
    navigate("/login");
    return;
  }

  setDrawerOpen(true);
};

  const [scrolled, setScrolled] = useState(false);
  const [hover, setHover] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`sticky top-0 z-40 transition-all duration-500 ${scrolled ? "bg-background/85 backdrop-blur-2xl border-b border-border" : "bg-transparent"}`}
        onMouseLeave={() => setHover(null)}
        data-testid="primary-navbar"
      >
        {/* Mobile / Tablet (<lg) — clean 3-zone flex */}
        <div className="lg:hidden flex items-center justify-between h-14 sm:h-16 px-4 sm:px-6">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 hover:text-brand-accent transition-colors"
            aria-label="Open menu"
            data-testid="nav-mobile-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>

          <Logo size="sm" className="absolute left-1/2 -translate-x-1/2" />

          <div className="flex items-center gap-0.5">
            <button onClick={toggleTheme} aria-label="Toggle theme" data-testid="nav-theme-toggle-mobile" className="p-2 hover:text-brand-accent transition-colors">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={handleCartClick} className="p-2 hover:text-brand-accent transition-colors relative" aria-label="Cart" data-testid="nav-cart-mobile">
              <ShoppingBag className="w-4 h-4" />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 bg-brand-accent text-background text-[9px] w-4 h-4 flex items-center justify-center" data-testid="nav-cart-count-mobile">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Desktop (≥lg) */}
        <div className="hidden lg:grid max-w-[1600px] mx-auto px-6 xl:px-10 grid-cols-3 items-center h-20">
          <nav className="flex items-center gap-8">
            {Object.keys(MEGA).map((key) => (
              <NavLink
                key={key}
                onMouseEnter={() => setHover(key)}
                onClick={() => navigate(`/shop/${key.toLowerCase()}`)}
                data-testid={`nav-${key.toLowerCase()}`}
              >
                {key}
              </NavLink>
            ))}
            <Link to="/shop" onMouseEnter={() => setHover(null)} className="ie-overline relative py-2 group hover:text-brand-accent transition-colors" data-testid="nav-shop-all">
              All
              <span className="absolute bottom-1 left-0 right-0 h-px bg-current origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </Link>
            <Link to="/shop?sort=newest" onMouseEnter={() => setHover(null)} className="ie-overline relative py-2 group hover:text-brand-accent transition-colors" data-testid="nav-new">
              New In
              <span className="absolute bottom-1 left-0 right-0 h-px bg-current origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </Link>
          </nav>

          <div className="flex justify-center">
            <Logo size="md" />
          </div>

          <div className="flex items-center justify-end gap-3">
            <button onClick={() => setSearchOpen(true)} className="p-2 hover:text-brand-accent transition-colors" aria-label="Search" data-testid="nav-search">
              <Search className="w-4 h-4" />
            </button>
            <button onClick={toggleTheme} aria-label="Toggle theme" data-testid="nav-theme-toggle" className="p-2 hover:text-brand-accent transition-colors">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Link to={user ? "/profile" : "/login"} className="p-2 hover:text-brand-accent transition-colors" aria-label="Account" data-testid="nav-account">
              <User className="w-4 h-4" />
            </Link>
            <Link to="/profile" className="p-2 hover:text-brand-accent transition-colors relative" aria-label="Wishlist" data-testid="nav-wishlist">
              <Heart className="w-4 h-4" />
              {wishCount > 0 && <span className="absolute -top-0.5 -right-0.5 text-[9px] tracking-wider bg-brand-accent text-background w-4 h-4 flex items-center justify-center">{wishCount}</span>}
            </Link>
            <button onClick={handleCartClick } className="p-2 hover:text-brand-accent transition-colors relative" aria-label="Cart" data-testid="nav-cart">
              <ShoppingBag className="w-4 h-4" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-brand-accent text-background text-[9px] w-4 h-4 flex items-center justify-center" data-testid="nav-cart-count">
                  {cartCount}
                </span>
              )}
            </button>
            {user && (
              <button onClick={() => { logout(); navigate("/"); }} className="ie-overline ml-2 hover:text-brand-accent" data-testid="nav-logout">
                Logout
              </button>
            )}
          </div>
        </div>

        {/* Mega menu */}
        <AnimatePresence>
          {hover && MEGA[hover] && (
            <motion.div
              key={hover}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onMouseEnter={() => setHover(hover)}
              className="absolute left-0 right-0 top-full bg-background/95 backdrop-blur-2xl border-t border-b border-border hidden lg:block"
              data-testid={`megamenu-${hover.toLowerCase()}`}
            >
              <div className="max-w-[1600px] mx-auto px-10 py-12 grid grid-cols-12 gap-12">
                {MEGA[hover].columns.map((col, i) => (
                  <div key={i} className="col-span-2">
                    <div className="ie-overline text-brand-accent mb-4">{col.title}</div>
                    <ul className="space-y-3">
                      {col.links.map(([label, href]) => (
                        <li key={label}>
                          <Link to={href} className="font-serif text-xl hover:text-brand-accent transition-colors" data-testid={`megamenu-link-${label.toLowerCase().replace(/\s+/g,'-')}`}>
                            {label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className="col-span-6 relative h-72 overflow-hidden">
                  <img src={MEGA[hover].image} alt={hover} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-6 left-6 text-white">
                    <div className="ie-overline mb-2 opacity-80">The {hover} Edit</div>
                    <div className="font-serif text-3xl">Quietly composed</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-50" onClick={() => setMobileOpen(false)} />
            <motion.div
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ ease: [0.16,1,0.3,1], duration: 0.5 }}
              className="fixed top-0 bottom-0 left-0 w-[88%] max-w-sm bg-background z-50 flex flex-col overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between p-5 sm:p-6 border-b border-border">
                <Logo size="sm" />
                <button onClick={() => setMobileOpen(false)} aria-label="close" data-testid="mobile-menu-close" className="p-2 -mr-2"><X className="w-5 h-5"/></button>
              </div>
              <nav className="flex-1 overflow-y-auto px-5 sm:px-6 py-8 space-y-8">
                <button onClick={() => { setSearchOpen(true); setMobileOpen(false); }} className="flex items-center gap-3 ie-overline text-text-secondary hover:text-foreground" data-testid="mobile-search-trigger">
                  <Search className="w-4 h-4"/> Search the atelier
                </button>
                {Object.entries(MEGA).map(([key, val]) => (
                  <div key={key}>
                    <div className="ie-overline text-brand-accent mb-4">{key}</div>
                    <ul className="space-y-3">
                      {val.columns[1].links.map(([label, href]) => (
                        <li key={label}>
                          <Link to={href} onClick={() => setMobileOpen(false)} className="font-serif text-2xl">{label}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className="border-t border-border pt-6 space-y-3">
                  <Link to="/track" onClick={() => setMobileOpen(false)} className="block ie-overline" data-testid="mobile-track">Track Order</Link>
                  {user ? (
                    <>
                      <Link to="/profile" onClick={() => setMobileOpen(false)} className="block ie-overline">My Account</Link>
                      <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="ie-overline" data-testid="mobile-logout">Logout</button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setMobileOpen(false)} className="block ie-overline">Login</Link>
                      <Link to="/signup" onClick={() => setMobileOpen(false)} className="block ie-overline">Create Account</Link>
                    </>
                  )}
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <CartDrawer />
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
